import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import IconView from "../IconView/IconView";

import { useTooltipContext } from "@/shared/components";
import { useWhatInputValueIsLastPointerMouse } from "../../what-input/useWhatInput";

import { trottleCheck } from "../../utils/optimization";
import { selectorSetting } from "../../store/settings/selectors";
import useOnlineOnlyHandler from "../../hooks/useOnlineOnlyHandler";
import { SETTING } from "../../utils/settings/constants";
import { handleKeyDownSelectItem } from "../../utils/keyboard-navigation/keyboard-navigation";
import { ICON_BUTTON_COLOR } from "./IconButton.constants";
import Ripple from "@/shared/components/Button/Ripple";

import styles from "./IconButton.module.scss";

const preventEvent = (e) => {
  e.preventDefault();
};

const IconButton = ({
  color,
  icon,
  showIcon,
  active,
  disabled = false,
  isOnLineOnly,
  onClick,
  onKeyDown,
  onDisableClick,
  className,
  classNameActive,
  title,
  ariaLabel,
  role = "button",
  ariaPressed = undefined,
  bordered,
  noHoverBackground,
  noFocusable,
  trottle = 0,
  children,
  ...otherProps
}) => {
  const classes = ["icon-button-base", styles["root"]];
  const trottleRef = useRef(Date.now());

  const handler = useOnlineOnlyHandler();
  const handleClick = isOnLineOnly ? handler(onClick, preventEvent) : onClick;

  const isLastPointerMouse = useWhatInputValueIsLastPointerMouse();

  const textMode = useSelector(selectorSetting(SETTING.textMode.id));

  const showTextInsteadOfIcon = textMode && title && !showIcon;

  if (color) {
    classes.push(styles[color]);
  }

  if (active) {
    classes.push("icon-button-active", styles["active"], "active");
    if (classNameActive) {
      classes.push(classNameActive);
    }
  } else if (disabled) {
    classes.push("icon-button-disabled", styles["disabled"]);
  }
  if (bordered) {
    classes.push("bordered", styles["bordered"]);
  }
  if (noHoverBackground) {
    classes.push("noHoverBackground", styles["noHoverBackground"]);
  }
  if (className) {
    classes.push(className);
  }
  if (showTextInsteadOfIcon) {
    classes.push("text-only", styles["text-only"]);
  }

  const { hideTooltip, showTooltip } = useTooltipContext();

  // Hide a tooltip on unmount.
  // FIXME: Need to specify a unique id, otherwise an unrelated tooltip may be closed.
  useEffect(() => {
    return () => {
      hideTooltip();
    };
  }, [hideTooltip]);

  const label = ariaLabel || title;
  let onClickFinal = undefined;

  if (onClick && !disabled) {
    onClickFinal = (e) => {
      if (trottleCheck(trottle, trottleRef.current)) {
        hideTooltip();
        trottleRef.current = Date.now();
        handleClick(e);
      }
    };
  } else if (onDisableClick && disabled) {
    onClickFinal = (e) => {
      if (trottleCheck(trottle, trottleRef.current)) {
        hideTooltip();
        onDisableClick(e);
      }
    };
  }

  const handleKeyDown = (e) => {
    if (onKeyDown) {
      onKeyDown(e);
    }

    if (!e.defaultPrevented && onClickFinal) {
      handleKeyDownSelectItem(e, onClickFinal);
    }
  };

  const allProps = {
    className: classes.join(" "),
    icon,
    tabIndex: noFocusable ? -1 : disabled ? undefined : 0,
    "aria-label": label,
    "aria-pressed": active ?? ariaPressed,
    role,
    onKeyDown: handleKeyDown,

    onClick: onClickFinal,
    ...otherProps,
  };

  if (showTextInsteadOfIcon) {
    return (
      <span {...allProps}>
        {label}
        {children}
      </span>
    );
  }

  allProps.icon = icon;
  allProps.onMouseEnter = isLastPointerMouse
    ? (e) => {
        if (title) {
          showTooltip(title, e, null);
        }
      }
    : undefined;

  allProps.children = children ? (
    <>
      {children}
      <Ripple />
    </>
  ) : (
    <Ripple />
  );

  return <IconView {...allProps} />;
};

IconButton.color = ICON_BUTTON_COLOR;

IconButton.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.func, PropTypes.number]).isRequired,
  active: PropTypes.bool,
  bordered: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  title: PropTypes.string,
  onClick: PropTypes.func,
  showIcon: PropTypes.bool,
  ariaPressed: PropTypes.bool,
  noFocusable: PropTypes.bool,
  ariaLabel: PropTypes.string,
  role: PropTypes.string,
  onDisableClick: PropTypes.func,
  trottle: PropTypes.number,
  isOnLineOnly: PropTypes.bool,
};

export default IconButton;
